<template>
  <div class="header">
    <h1>Help us confirm it’s really you.</h1>
    <p class="mt-[10px]">
      {{
        isOtpType === 'emailOtp'
          ? 'Please enter the Email OTP sent to'
          : 'Please enter the 6-digit code sent to'
      }}
      <br />
      <span class="font-black tracking-[-0.03em]">{{
        isOtpType === 'emailOtp' ? email : mobileNumber
      }}</span>
    </p>
  </div>

  <div class="pt-[40px]">
    <div ref="otpInputs" class="w-full flex justify-between mb-[16px] sm:mb-[24px]">
      <v-otp-input
        ref="otpInput"
        input-classes="otp-input error-input"
        input-type="number"
        inputmode="numeric"
        separator=" "
        :num-inputs="6"
        :should-auto-focus="true"
        :is-disabled="isDisabled"
        @on-complete="handleOnComplete"
      />
    </div>

    <div v-if="hasError" class="col-span-12 mb-[32px]">
      <div class="otp-error">
        {{ otpValidError }}
      </div>
    </div>

    <p
      class="text-[1rem] leading-[1.5rem] sm:mb-[40px] mt-[32px] mb-[36px] text-[#000000] resend-text"
      v-if="!showResendButton"
    >
      {{ isOtpType === 'emailOtp' ? 'Resend Email OTP' : 'Resend Code' }} in
      {{ showResendButtonCount }} sec
    </p>

    <p
      class="text-[1rem] leading-[1.5rem] sm:mb-[40px] mt-[32px] mb-[36px] resend-text underline"
      :class="isResending ? 'text-[#a0a0a0] cursor-not-allowed' : 'text-[#454BEF] cursor-pointer'"
      v-if="showResendButton"
      @click="!isResending && sendOtp()"
    >
      {{
        isResending ? 'Sending...' : isOtpType === 'emailOtp' ? 'Resend Email OTP' : 'Resend Code'
      }}
    </p>
  </div>
  <div v-html="alertContent" class="bg-[#f6f4f0]"></div>
</template>

<script>
import {
  OTP_RESEND_TIME,
  NUMBER_OF_OTP,
  ONE_SEC,
  THREE_SEC,
  INITIATE_VERIFY_EMAIL_OTP_URL,
  INITIATE_VERIFY_EMAIL_OTP_NON_PUBLIC_URL,
  INITIATE_VERIFY_MOBILE_PHONE_OTP_URL,
  VERIFY_EMAIL_OTP,
  VERIFY_EMAIL_OTP_NON_PUBLIC_URL,
  VERIFY_PHONE_OTP,
} from '@/constants/registration-endpoints';
import { ERROR_VALID_OTP, MESSAGE_OTP_SENT } from '@/constants/validation/error-messages';
import { ref, onMounted } from 'vue';
import VOtpInput from 'vue3-otp-input';
import axios from 'axios';
import { mapState } from 'vuex';

export default {
  components: {
    VOtpInput,
  },
  props: ['isOtpType', 'email', 'mobileNumber', 'countryCode', 'isOnboarding', 'emailValidated'],
  data() {
    return {
      isVisibleAlert: false,
      alertContent: '',
      nativeAutofillListener: null,
    };
  },
  computed: {
    ...mapState('user', ['token']),
  },
  watch: {},
  mounted() {
    this.$nextTick(() => {
      this.attachNativeAutofillHandler();
    });
  },
  setup(props, { emit }) {
    const showResendButton = ref(false);
    const showResendButtonCount = ref(OTP_RESEND_TIME);
    const otpValidError = ref(ERROR_VALID_OTP);
    const otpInput = ref(null);
    const isDisabled = ref(false);
    const hasError = ref(false);
    const isResending = ref(false);

    onMounted(() => {});

    const clearInput = () => {
      otpInput.value.clearInput();
    };
    return {
      showResendButton,
      showResendButtonCount,
      clearInput,
      otpInput,
      isDisabled,
      hasError,
      otpValidError,
      isResending,
    };
  },
  methods: {
    async sendOtp() {
      if (this.isResending) return;

      this.isResending = true;
      this.showResendButton = false;

      try {
        const apiEndpoint =
          this.isOtpType === 'emailOtp'
            ? this.isOnboarding
              ? process.env.VUE_APP_API_URL + INITIATE_VERIFY_EMAIL_OTP_NON_PUBLIC_URL
              : process.env.VUE_APP_API_URL + INITIATE_VERIFY_EMAIL_OTP_URL
            : process.env.VUE_APP_API_URL + INITIATE_VERIFY_MOBILE_PHONE_OTP_URL;
        const payload =
          this.isOtpType === 'emailOtp'
            ? this.isOnboarding && this.emailValidated === 0
              ? { email: this.email, emailValidated: false }
              : { email: this.email }
            : {
                contactNumber: this.mobileNumber,
                countryCode: this.countryCode,
              };
        // API call to send otp
        const { status, data } = await axios.post(apiEndpoint, payload, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
        if (status !== 200) throw new Error('Failed in getting just for you');

        // Alert
        this.renderAlertEl({
          type: 'success',
          message: MESSAGE_OTP_SENT,
        });

        // 60 sec timer - only start after successful send
        const timer = setInterval(() => {
          this.showResendButtonCount--;
          if (this.showResendButtonCount === 0) {
            this.showResendButton = true;
            clearInterval(timer);
            this.showResendButtonCount = OTP_RESEND_TIME;
          }
        }, ONE_SEC);
      } catch (e) {
        this.renderAlertEl({
          type: 'error',
          message: e.response.data.message,
        });
        // Re-enable resend button on error
        this.showResendButton = true;
      } finally {
        this.isResending = false;
      }
    },
    renderAlertEl(config) {
      const { type, message } = config;
      const alertClass = type === 'success' ? 'bg-emerald-500' : 'bg-red-500';
      this.alertContent =
        '<div role="alert"class="relative flex w-full p-3 text-sm text-white ' +
        alertClass +
        ' rounded-lg">' +
        '<strong>' +
        message +
        '</strong>' +
        '</div>';

      // After 2 seconds, clear the content (send blank)
      setTimeout(() => {
        this.alertContent = ''; // Clear content
      }, THREE_SEC);
    },
    async handleOnComplete(value) {
      this.hasError = false;
      const apiEndpoint =
        this.isOtpType === 'emailOtp'
          ? this.isOnboarding
            ? process.env.VUE_APP_API_URL + VERIFY_EMAIL_OTP_NON_PUBLIC_URL
            : process.env.VUE_APP_API_URL + VERIFY_EMAIL_OTP
          : process.env.VUE_APP_API_URL + VERIFY_PHONE_OTP;

      const payLoad =
        this.isOtpType === 'emailOtp'
          ? { email: this.email, otp: value }
          : {
              contactNumber: this.mobileNumber,
              countryCode: this.countryCode,
              otp: value,
            };

      try {
        await axios.post(apiEndpoint, payLoad, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });
        this.$emit('nextButtonHandler');
      } catch (e) {
        this.clearInput();

        if (e.response && (e.response.status === 403 || e.response.status === 500)) {
          if (e.response.data.message.includes('Incorrect OTP or OTP expired')) {
            this.hasError = true;
          } else {
            this.renderAlertEl({
              type: 'error',
              message: e.response.data.message,
            });
          }
          return;
        }

        // Anything else (400 validation, network drop) used to be swallowed
        // entirely, leaving the sheet looking alive but doing nothing.
        const message = e.response?.data?.message;
        this.renderAlertEl({
          type: 'error',
          message: Array.isArray(message)
            ? message.join(', ')
            : message || 'Could not verify that code. Please try again.',
        });
      }
    },
    attachNativeAutofillHandler() {
      const container = this.$refs.otpInputs;
      const otpRef = this.$refs.otpInput;

      if (!container || !otpRef) return;

      // Always start fresh to avoid duplicate handlers when the component remounts.
      this.detachNativeAutofillHandler();

      const firstInput = container.querySelector('input');
      if (!firstInput) return;

      // Hint to iOS Safari / Apple Mail assistant that this field accepts OTP codes.
      firstInput.setAttribute('autocomplete', 'one-time-code');
      firstInput.setAttribute('autocorrect', 'off');
      firstInput.setAttribute('autocapitalize', 'off');

      const handlePrefill = event => {
        const rawValue =
          typeof event.data === 'string' && event.data.length > 1
            ? event.data
            : event.target?.value;

        this.applyNativePrefill(rawValue);
      };

      firstInput.addEventListener('input', handlePrefill, true);
      firstInput.addEventListener('change', handlePrefill, true);

      this.nativeAutofillListener = {
        input: firstInput,
        handler: handlePrefill,
      };
    },
    detachNativeAutofillHandler() {
      if (!this.nativeAutofillListener) return;

      const { input, handler } = this.nativeAutofillListener;
      input.removeEventListener('input', handler, true);
      input.removeEventListener('change', handler, true);
      this.nativeAutofillListener = null;
    },
    applyNativePrefill(rawValue) {
      if (!rawValue) return;

      const digitsOnly = rawValue.replace(/\D/g, '');
      if (digitsOnly.length <= 1) return;

      const limit = NUMBER_OF_OTP || 6;
      const prefillValue = digitsOnly.slice(0, limit);

      if (this.$refs?.otpInput?.fillInput) {
        this.$refs.otpInput.fillInput(prefillValue);
      }
    },
  },
  beforeMount() {
    this.sendOtp();
  },
  beforeUnmount() {
    this.detachNativeAutofillHandler();
  },
};
</script>

<style>
.otp-header {
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
}

.otp-input-container {
  display: flex;
  width: 100vw;
  justify-content: space-between;
}

.otp-input {
  width: 48px;
  height: 69px;
  /* width: 55.5px;
  height: 79px; */
  border-radius: 12px;
  border-width: 1px;
  border-color: #d8dadc;
  display: inline-block;
  text-align: center;
  background: #f6f4f0;

  @media screen and (min-width: 1024px) {
    width: 64px;
    height: 64px;
    margin-right: 16px;
  }
}

input:focus {
  border: 1.1px solid rgba(69, 75, 239, 1);
}

input:focus-visible {
  border: 1.1px solid rgba(69, 75, 239, 1);
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}

.resend-text {
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  vertical-align: bottom;
}

.otp-error {
  font-weight: 400;
  font-style: italic !important;
  font-size: 16px;
  line-height: 24px;
  color: #ec4e20;
}
</style>
